/**
 * E-Learning Resources Page - ISOCNTRL
 * Learning paths, training documents, progress tracking
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Search, GraduationCap, BookOpen, Video, Calendar,
  Download, CheckSquare, Square, Plus, Clock, File,
  ChevronRight, Play, BarChart3, Lock, Award, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import useAuthStore from '../lib/auth-store';
import AdminProgressTracker from '../components/AdminProgressTracker';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function ProgressBar({ percent, className = '' }) {
  const color = percent >= 75 ? 'bg-green-500' : percent >= 25 ? 'bg-yellow-500' : 'bg-[#3b82f6]';
  return (
    <div className={`w-full bg-gray-200 rounded-full h-2 ${className}`}>
      <div className={`h-2 rounded-full transition-all ${color}`} style={{ width: `${percent}%` }} />
    </div>
  );
}

function FileTypeIcon({ fileType }) {
  const colorMap = {
    PDF: 'bg-red-100 text-red-600',
    DOCX: 'bg-blue-100 text-blue-600',
    XLS: 'bg-green-100 text-green-600',
    PPT: 'bg-orange-100 text-orange-600',
  };
  const classes = colorMap[fileType] || 'bg-gray-100 text-gray-600';
  return (
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${classes}`}>
      <File className="h-5 w-5" />
    </div>
  );
}

export default function ELearningPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [paths, setPaths] = useState([]);
  const [trainingDocs, setTrainingDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [fileTypeFilter, setFileTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Admin dialogs
  const [pathDialogOpen, setPathDialogOpen] = useState(false);
  const [lessonDialogOpen, setLessonDialogOpen] = useState(false);
  const [docUploadOpen, setDocUploadOpen] = useState(false);
  const [newPath, setNewPath] = useState({ title: '', description: '', target_role: 'all', estimated_hours: 0 });
  const [newLesson, setNewLesson] = useState({ title: '', description: '', content_type: 'video', video_url: '', text_content: '', duration_minutes: 0 });
  const [selectedPathId, setSelectedPathId] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploadCategory, setUploadCategory] = useState('General');

  const isAdmin = user?.role === 'super_admin' || user?.role === 'iso';

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const loadPaths = useCallback(async () => {
    try {
      const url = roleFilter !== 'all'
        ? `${API_BASE}/api/elearning/paths?role_filter=${roleFilter}`
        : `${API_BASE}/api/elearning/paths`;
      const res = await fetch(url, { headers: getHeaders() });
      if (res.ok) setPaths(await res.json());
    } catch (err) { console.error(err); }
  }, [getHeaders, roleFilter]);

  const loadTrainingDocs = useCallback(async () => {
    try {
      let url = `${API_BASE}/api/elearning/training-docs?`;
      if (categoryFilter !== 'all') url += `category=${categoryFilter}&`;
      if (fileTypeFilter !== 'all') url += `file_type=${fileTypeFilter}&`;
      const res = await fetch(url, { headers: getHeaders() });
      if (res.ok) setTrainingDocs(await res.json());
    } catch (err) { console.error(err); }
  }, [getHeaders, categoryFilter, fileTypeFilter]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadPaths(), loadTrainingDocs()]);
      setLoading(false);
    };
    init();
  }, [loadPaths, loadTrainingDocs]);

  const handleToggleDocComplete = async (docId) => {
    try {
      const res = await fetch(`${API_BASE}/api/elearning/training-docs/${docId}/toggle-complete`, {
        method: 'POST',
        headers: getHeaders()
      });
      if (res.ok) {
        const { completed } = await res.json();
        setTrainingDocs(prev => prev.map(d => d.id === docId ? { ...d, completed } : d));
      }
    } catch (err) { toast.error('Failed to update'); }
  };

  const handleDownloadDoc = async (doc) => {
    if (!doc.storage_path) {
      toast.info('This is a demo document without a file');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/elearning/training-docs/${doc.id}/download`, { headers: getHeaders() });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleCreatePath = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/elearning/paths`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(newPath)
      });
      if (res.ok) {
        toast.success('Learning path created');
        setPathDialogOpen(false);
        setNewPath({ title: '', description: '', target_role: 'all', estimated_hours: 0 });
        loadPaths();
      }
    } catch { toast.error('Failed to create'); }
  };

  const handleCreateLesson = async () => {
    if (!selectedPathId) return;
    try {
      const res = await fetch(`${API_BASE}/api/elearning/paths/${selectedPathId}/lessons`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(newLesson)
      });
      if (res.ok) {
        toast.success('Lesson added');
        setLessonDialogOpen(false);
        setNewLesson({ title: '', description: '', content_type: 'video', video_url: '', text_content: '', duration_minutes: 0 });
        loadPaths();
      }
    } catch { toast.error('Failed to create'); }
  };

  const handleUploadTrainingDoc = async () => {
    if (!uploadFile || !uploadTitle) return;
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadTitle);
      formData.append('description', uploadDesc);
      formData.append('category', uploadCategory);
      const res = await fetch(`${API_BASE}/api/elearning/training-docs/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });
      if (res.ok) {
        toast.success('Document uploaded');
        setDocUploadOpen(false);
        setUploadFile(null);
        setUploadTitle('');
        loadTrainingDocs();
      }
    } catch { toast.error('Upload failed'); }
  };

  const categories = [...new Set(trainingDocs.map(d => d.category).filter(Boolean))];
  const fileTypes = [...new Set(trainingDocs.map(d => d.file_type).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="elearning-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">E-Learning Resources</h1>
          <p className="text-muted-foreground mt-1">
            Access training materials, videos, and guides to enhance your skills
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search resources..."
              className="pl-9 w-56"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="elearning-search"
            />
          </div>
          {isAdmin && (
            <Button
              onClick={() => setPathDialogOpen(true)}
              className="bg-[#3b82f6] hover:bg-[#2563eb]"
              data-testid="add-path-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Content
            </Button>
          )}
        </div>
      </div>

      {/* Learning Paths */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Learning Paths</h2>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-36" data-testid="role-filter">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="iso_agent">Sales Rep</SelectItem>
              <SelectItem value="iso">Administrator</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {paths.map(path => {
            const isLocked = path.is_locked;
            const isCertified = path.is_certified;
            return (
              <Card key={path.id} className={`p-5 flex flex-col relative ${isLocked ? 'opacity-60' : ''}`} data-testid={`path-card-${path.id}`}>
                {isLocked && (
                  <div className="absolute inset-0 bg-background/50 backdrop-blur-[1px] rounded-lg z-10 flex flex-col items-center justify-center">
                    <Lock className="h-8 w-8 text-muted-foreground mb-2" />
                    <p className="text-sm font-medium text-muted-foreground">Complete prerequisite first</p>
                  </div>
                )}
                {isCertified && (
                  <div className="absolute -top-2 -right-2 z-20">
                    <div className="bg-green-500 text-white rounded-full p-1.5">
                      <Award className="h-4 w-4" />
                    </div>
                  </div>
                )}
                <ProgressBar percent={path.progress_pct} className="mb-3" />
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-bold text-base">{path.title}</h3>
                  <Badge variant="secondary" className="text-xs shrink-0 ml-2">
                    {path.completed_lessons}/{path.total_lessons} Completed
                  </Badge>
                </div>
                {path.certification_title && (
                  <div className="flex items-center gap-1.5 mb-2">
                    <Shield className="h-3.5 w-3.5 text-[#3b82f6]" />
                    <span className="text-xs text-[#3b82f6] font-medium">
                      Certified {path.certification_title}
                    </span>
                  </div>
                )}
                <p className="text-sm text-muted-foreground mb-3 flex-1">{path.description}</p>
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                  <span>{path.progress_pct}% complete</span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Est. time: {path.estimated_hours}h
                  </span>
                </div>
                <div className="flex gap-2">
                  {isCertified ? (
                    <Button
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => navigate(`/elearning/${path.id}`)}
                      data-testid={`review-path-${path.id}`}
                    >
                      <Award className="h-4 w-4 mr-2" />
                      Review Course
                    </Button>
                  ) : (
                    <Button
                      className="flex-1 bg-[#3b82f6] hover:bg-[#2563eb]"
                      onClick={() => !isLocked && navigate(`/elearning/${path.id}`)}
                      disabled={isLocked}
                      data-testid={`continue-learning-${path.id}`}
                    >
                      {isLocked ? (
                        <><Lock className="h-4 w-4 mr-2" />Locked</>
                      ) : path.progress_pct > 0 ? 'Continue Learning' : 'Start Learning'}
                    </Button>
                  )}
                  {isAdmin && !isLocked && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => { setSelectedPathId(path.id); setLessonDialogOpen(true); }}
                      data-testid={`add-lesson-${path.id}`}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Tabs: Documents, Videos, Webinars, Progress (admin) */}
      <Tabs defaultValue="documents" className="space-y-6">
        <TabsList>
          <TabsTrigger value="documents">
            <BookOpen className="h-4 w-4 mr-2" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="videos">
            <Video className="h-4 w-4 mr-2" />
            Videos
          </TabsTrigger>
          <TabsTrigger value="webinars">
            <Calendar className="h-4 w-4 mr-2" />
            Webinars
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="progress" data-testid="progress-tab">
              <BarChart3 className="h-4 w-4 mr-2" />
              Progress Tracker
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="documents">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Training Documents</h3>
            <div className="flex items-center gap-2">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={fileTypeFilter} onValueChange={setFileTypeFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="All File Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All File Types</SelectItem>
                  {fileTypes.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
              {isAdmin && (
                <Button size="sm" variant="outline" onClick={() => setDocUploadOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" /> Upload
                </Button>
              )}
            </div>
          </div>

          <Card>
            {trainingDocs.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">No training documents yet</div>
            ) : (
              <div className="divide-y">
                {trainingDocs.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-4 hover:bg-accent/30 transition-colors" data-testid={`training-doc-${doc.id}`}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <FileTypeIcon fileType={doc.file_type} />
                      <div className="min-w-0">
                        <p className="font-medium text-sm">{doc.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{doc.description}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {doc.category && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{doc.category}</Badge>}
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{doc.file_type}</Badge>
                          <span className="text-[10px] text-muted-foreground">{doc.file_size_label}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <button
                        onClick={() => handleToggleDocComplete(doc.id)}
                        className="flex items-center gap-2 text-sm hover:text-foreground"
                        data-testid={`toggle-complete-${doc.id}`}
                      >
                        {doc.completed
                          ? <CheckSquare className="h-5 w-5 text-[#3b82f6]" />
                          : <Square className="h-5 w-5 text-muted-foreground" />
                        }
                        <span className="text-xs text-muted-foreground">Mark as completed</span>
                      </button>
                      <Button variant="ghost" size="icon" onClick={() => handleDownloadDoc(doc)}>
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="videos">
          <Card className="p-8 text-center">
            <Video className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-40" />
            <p className="text-muted-foreground mb-2">Videos are available within Learning Paths</p>
            <p className="text-sm text-muted-foreground">
              Select a learning path above to access video lessons
            </p>
          </Card>
        </TabsContent>

        <TabsContent value="webinars">
          <Card className="p-8 text-center">
            <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-40" />
            <p className="text-muted-foreground mb-2">Recorded webinars coming soon</p>
            <p className="text-sm text-muted-foreground">
              Webinar recordings will be available here once uploaded by admin
            </p>
          </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="progress">
            <AdminProgressTracker />
          </TabsContent>
        )}
      </Tabs>
      <Dialog open={pathDialogOpen} onOpenChange={setPathDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Learning Path</DialogTitle>
            <DialogDescription>Add a new learning path for your team</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input value={newPath.title} onChange={e => setNewPath({ ...newPath, title: e.target.value })} placeholder="e.g., Sales Representative" className="mt-1" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={newPath.description} onChange={e => setNewPath({ ...newPath, description: e.target.value })} placeholder="Brief description" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Target Role</Label>
                <Select value={newPath.target_role} onValueChange={v => setNewPath({ ...newPath, target_role: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="iso_agent">Sales Rep</SelectItem>
                    <SelectItem value="iso">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Est. Hours</Label>
                <Input type="number" value={newPath.estimated_hours} onChange={e => setNewPath({ ...newPath, estimated_hours: parseFloat(e.target.value) || 0 })} className="mt-1" />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setPathDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreatePath} disabled={!newPath.title} className="bg-[#3b82f6] hover:bg-[#2563eb]">Create</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Lesson Dialog */}
      <Dialog open={lessonDialogOpen} onOpenChange={setLessonDialogOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Add Lesson</DialogTitle>
            <DialogDescription>Add a new lesson to the learning path</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input value={newLesson.title} onChange={e => setNewLesson({ ...newLesson, title: e.target.value })} placeholder="Lesson title" className="mt-1" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={newLesson.description} onChange={e => setNewLesson({ ...newLesson, description: e.target.value })} placeholder="Brief description" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Content Type</Label>
                <Select value={newLesson.content_type} onValueChange={v => setNewLesson({ ...newLesson, content_type: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="document">Document</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Duration (min)</Label>
                <Input type="number" value={newLesson.duration_minutes} onChange={e => setNewLesson({ ...newLesson, duration_minutes: parseInt(e.target.value) || 0 })} className="mt-1" />
              </div>
            </div>
            {newLesson.content_type === 'video' && (
              <div>
                <Label>Video URL (YouTube/Vimeo/Loom)</Label>
                <Input value={newLesson.video_url} onChange={e => setNewLesson({ ...newLesson, video_url: e.target.value })} placeholder="https://youtube.com/embed/..." className="mt-1" />
              </div>
            )}
            <div>
              <Label>Text Content / Notes</Label>
              <textarea
                value={newLesson.text_content}
                onChange={e => setNewLesson({ ...newLesson, text_content: e.target.value })}
                placeholder="Lesson content or description..."
                className="mt-1 w-full min-h-[80px] rounded-md border px-3 py-2 text-sm"
                rows={4}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setLessonDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateLesson} disabled={!newLesson.title} className="bg-[#3b82f6] hover:bg-[#2563eb]">Add Lesson</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Training Doc Dialog */}
      <Dialog open={docUploadOpen} onOpenChange={setDocUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Training Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} placeholder="Document title" className="mt-1" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={uploadDesc} onChange={e => setUploadDesc(e.target.value)} placeholder="Brief description" className="mt-1" />
            </div>
            <div>
              <Label>Category</Label>
              <Input value={uploadCategory} onChange={e => setUploadCategory(e.target.value)} placeholder="e.g., Sales, Compliance" className="mt-1" />
            </div>
            <div>
              <Label>File *</Label>
              <Input type="file" onChange={e => {
                const f = e.target.files[0];
                setUploadFile(f);
                if (f && !uploadTitle) setUploadTitle(f.name.replace(/\.[^/.]+$/, ''));
              }} className="mt-1" />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDocUploadOpen(false)}>Cancel</Button>
            <Button onClick={handleUploadTrainingDoc} disabled={!uploadFile || !uploadTitle} className="bg-[#3b82f6] hover:bg-[#2563eb]">Upload</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
